import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronRight } from 'lucide-react';
import { categoryHierarchy } from '../mockData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isInHero, setIsInHero] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isMegaMenuMobileOpen, setIsMegaMenuMobileOpen] = useState(false);
  const location = useLocation();
  const megaMenuRef = useRef(null);
  const productsLinkRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
      
      // Check if we're in hero section (only on home page)
      if (location.pathname === '/') {
        setIsInHero(scrollY < window.innerHeight * 0.8);
      } else {
        setIsInHero(false);
      }
    };
    
    // Initial check
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Keyboard accessibility for mega menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMegaMenuOpen) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setIsMegaMenuOpen(false);
        productsLinkRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMegaMenuOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Delayed close handlers for mega menu
  const handleMegaMenuEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
      closeTimeoutRef.current = null;
    }, 200);
  };

  const closeAllMenus = () => {
    setIsMegaMenuOpen(false);
    setIsMobileMenuOpen(false);
    setIsMegaMenuMobileOpen(false);
  };

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'PRODUCTS', path: '/products', hasMegaMenu: true },
    { name: 'GALLERY', path: '/gallery' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' }
  ];

  return (
    <nav className="fixed top-4 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* MAIN GLASS BAR */}
        <div
          className={`flex items-center justify-between h-14 px-6 rounded-full transition-all duration-300
            border backdrop-blur-xl shadow-lg
            ${
              isInHero
                ? 'bg-white/20 border-white/30 shadow-black/10'
                : 'bg-white/25 border-white/40 shadow-black/10'
            }
            relative
          `}
        >
          {/* Subtle gradient overlay for better contrast */}
          <div className={`absolute inset-0 rounded-full transition-opacity duration-300
            ${
              isInHero
                ? 'bg-gradient-to-br from-white/30 via-white/20 to-white/10 opacity-100'
                : 'bg-gradient-to-br from-white/40 via-white/30 to-white/20 opacity-100'
            }
          `} />
          
          {/* Content with relative positioning */}
          <div className="relative z-10 flex items-center justify-between w-full">
            {/* LOGO */}
            <Link to="/" className="flex items-center group">
              <span className={`text-xl font-semibold tracking-tight transition-all duration-500
                ${
                  isInHero
                    ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
                    : 'bg-gradient-to-r from-[#222] via-[#C1A57B] to-[#222] bg-clip-text text-transparent'
                }
                group-hover:from-[#C1A57B]
                group-hover:via-[#B09568]
                group-hover:to-[#C1A57B]
              `}>
                AARTEMA
              </span>
            </Link>

            {/* DESKTOP NAV LINKS */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                const isActive = location.pathname.startsWith(link.path) && link.path !== '/' || location.pathname === link.path;
                const isProducts = link.hasMegaMenu;

                if (isProducts) {
                  return (
                    <div
                      key={link.path}
                      className="relative"
                      ref={productsLinkRef}
                      onMouseEnter={handleMegaMenuEnter}
                      onMouseLeave={handleMegaMenuLeave}
                    >
                      <Link
                        to={link.path}
                        className={`relative text-xs font-medium tracking-wide transition-all duration-300
                          ${
                            isInHero
                              ? isActive
                                ? 'text-[#C1A57B] drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]'
                                : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:text-[#C1A57B]'
                              : isActive
                                ? 'text-[#C1A57B]'
                                : 'text-[#222] hover:text-[#C1A57B]'
                          }
                        `}
                      >
                        {link.name}

                        {/* SUBTLE GOLD UNDERLINE */}
                        <span
                          className={`absolute left-0 -bottom-2 h-[1.5px] rounded-full bg-[#C1A57B]
                            transition-all duration-300
                            ${isActive || isMegaMenuOpen ? 'w-full' : 'w-0'}
                          `}
                        />
                      </Link>
                      
                      {/* MEGA MENU - DESKTOP */}
                      {isMegaMenuOpen && (
                        <div
                          ref={megaMenuRef}
                          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 z-[60] w-max max-w-[95vw]"
                          onMouseEnter={handleMegaMenuEnter}
                          onMouseLeave={handleMegaMenuLeave}
                        >
                          <div className="bg-white rounded-2xl shadow-xl border border-[#EDEDED] overflow-hidden animate-fadeInSlideDown px-10 py-8">
                            <div className="flex gap-12">
                              {Object.entries(categoryHierarchy).map(([category, subcategories]) => (
                                <div key={category} className="flex flex-col min-w-[150px]">
                                  {/* Category Header */}
                                  <Link
                                    to={`/products?category=${encodeURIComponent(category)}`}
                                    onClick={closeAllMenus}
                                    className="text-sm font-bold text-[#222] hover:text-[#C1A57B] mb-4 uppercase border-b border-gray-100 pb-2 transition-colors"
                                  >
                                    {category}
                                  </Link>
                                  
                                  {/* Subcategories List */}
                                  {subcategories.length > 0 && (
                                    <ul className="space-y-3">
                                      {subcategories.map((sub, idx) => (
                                        <li key={idx}>
                                          <Link
                                            to={`/products?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(sub)}`}
                                            onClick={closeAllMenus}
                                            className="text-[13px] text-gray-500 hover:text-[#C1A57B] transition-colors block"
                                          >
                                            {sub}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative text-xs font-medium tracking-wide transition-all duration-300
                      ${
                        isInHero
                          ? isActive
                            ? 'text-[#C1A57B] drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]'
                            : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:text-[#C1A57B]'
                          : isActive
                            ? 'text-[#C1A57B]'
                            : 'text-[#222] hover:text-[#C1A57B]'
                      }
                    `}
                  >
                    {link.name}
                    <span
                      className={`absolute left-0 -bottom-2 h-[1.5px] rounded-full bg-[#C1A57B]
                        transition-all duration-300
                        ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}
                      `}
                    />
                  </Link>
                );
              })}
            </div>

            {/* PHONE */}
            <a
              href="tel:+919350056070"
              className="hidden md:flex items-center gap-2 px-5 py-2 rounded-full
                bg-gradient-to-r from-[#C1A57B] to-[#B09568]
                text-white text-xs font-medium
                shadow-sm hover:shadow-md
                hover:scale-[1.03]
                transition-all duration-300"
            >
              <Phone size={14} />
              +91-9350056070
            </a>

            {/* MOBILE TOGGLE */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-full transition-all duration-300
                ${isInHero ? 'text-white hover:bg-white/10' : 'text-[#222] hover:bg-black/5'}
              `}
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        <div
          className={`md:hidden mt-3 rounded-2xl backdrop-blur-xl shadow-lg
            border overflow-hidden transition-all duration-300 relative
            ${
              isInHero
                ? 'bg-white/20 border-white/30 shadow-black/10'
                : 'bg-white/25 border-white/40 shadow-black/10'
            }
            ${isMobileMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className={`absolute inset-0 rounded-2xl transition-opacity duration-300
            ${
              isInHero
                ? 'bg-gradient-to-br from-white/30 via-white/20 to-white/10'
                : 'bg-gradient-to-br from-white/40 via-white/30 to-white/20'
            }
          `} />
          
          <div className="relative z-10 flex flex-col p-3 gap-1 overflow-y-auto max-h-[70vh]">
            {navLinks.map((link) => {
              const isProducts = link.hasMegaMenu;
              
              if (isProducts) {
                return (
                  <div key={link.path}>
                    <button
                      onClick={() => setIsMegaMenuMobileOpen(!isMegaMenuMobileOpen)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-xs font-medium transition
                        ${
                          isInHero
                            ? location.pathname.startsWith(link.path)
                              ? 'text-[#C1A57B] bg-white/10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
                              : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:bg-white/10'
                            : location.pathname.startsWith(link.path)
                              ? 'text-[#C1A57B] bg-white/10'
                              : 'text-[#222] hover:bg-white/5'
                        }
                      `}
                    >
                      <span>{link.name}</span>
                      <ChevronRight
                        size={14}
                        className={`transition-transform duration-300 ${
                          isMegaMenuMobileOpen ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                    
                    {/* Mobile Mega Menu (Hierarchical List) */}
                    <div
                      className={`overflow-hidden transition-all duration-300
                        ${isMegaMenuMobileOpen ? 'max-h-[1000px] opacity-100 mt-2' : 'max-h-0 opacity-0'}
                      `}
                    >
                      <div className="pl-6 pr-2 py-2 space-y-5">
                        {Object.entries(categoryHierarchy).map(([category, subcategories]) => (
                          <div key={category}>
                            <Link
                              to={`/products?category=${encodeURIComponent(category)}`}
                              onClick={closeAllMenus}
                              className={`block text-xs font-bold uppercase mb-2
                                ${isInHero ? 'text-[#C1A57B]' : 'text-[#C1A57B]'}
                              `}
                            >
                              {category}
                            </Link>
                            
                            {subcategories.length > 0 && (
                              <ul className={`space-y-3 border-l-2 pl-3 
                                ${isInHero ? 'border-white/20' : 'border-[#222]/10'}
                              `}>
                                {subcategories.map((sub, idx) => (
                                  <li key={idx}>
                                    <Link
                                      to={`/products?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(sub)}`}
                                      onClick={closeAllMenus}
                                      className={`block text-xs transition-colors
                                        ${isInHero ? 'text-white/80 hover:text-white' : 'text-gray-500 hover:text-[#222]'}
                                      `}
                                    >
                                      {sub}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeAllMenus}
                  className={`px-4 py-2.5 rounded-lg text-xs font-medium transition
                    ${
                      isInHero
                        ? location.pathname === link.path
                          ? 'text-[#C1A57B] bg-white/10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
                          : 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:bg-white/10'
                        : location.pathname === link.path
                          ? 'text-[#C1A57B] bg-white/10'
                          : 'text-[#222] hover:bg-white/5'
                    }
                  `}
                >
                  {link.name}
                </Link>
              );
            })}

            <a
              href="tel:+919350056070"
              className="mt-2 flex items-center justify-center gap-2 py-2.5 rounded-lg
                bg-gradient-to-r from-[#C1A57B] to-[#B09568]
                text-white text-xs font-medium shadow-sm"
            >
              <Phone size={14} />
              +91-9350056070
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
